import { Box, keyframes, styled } from "@mui/material";
import type { RenderStateCamera } from "@novorender/api";
import { Blueprint, Error as ErrorIcon, Home } from "@procore/core-icons";
import { Button } from "@procore/core-react";
import { vec2 } from "gl-matrix";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { MINIMAP_LAYOUT } from "components/toolbars/constants";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useResetView } from "features/home/useResetView";
import {
    selectIsMiniMapVisible,
    selectIsOnline,
    selectIsProcoreMobileDesign,
    selectMinimapTilingEnabled,
    selectProcoreContext,
    selectSceneId,
} from "slices/explorer";
import { AsyncStatus } from "types/misc";

import { downloadDrawingData } from "./downloadDrawingData";
import { LargeMapMobile } from "./largeMapMobile";
import {
    clearNavigationPin,
    selectCurrentDrawing,
    selectDrawingPlacements,
    selectIsLargeMapMobileOpen,
    selectMiniMapDrawings,
    selectMiniMapLevels,
    selectNavigationPin,
    setCurrentDrawing,
    setLargeMapMobileOpen,
} from "./minimapSlice";
import {
    clampMiniMapFollowLayout,
    drawPositionIndicator,
    mobileMiniMapImageScale,
    modelToDrawingSpace,
    POSITION_INDICATOR_HALF_SIZE,
    resolveSheetForCamera,
} from "./miniMapUtils";
import { buildTilingRequest } from "./tiledDrawing";
import type { DrawingSpacePosition } from "./types";
import { useDrawingImage } from "./useDrawingImage";
import { useMiniMapData } from "./useMiniMapData";

export const MINIMAP_MOBILE_ELEMENT_ID = "minimap-mobile-container";

const MiniMapContainer = styled(Box)({
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "8px",
    borderRadius: 8,
    background: "linear-gradient(179.92deg, rgba(244, 240, 240, 0.5) 97.81%, rgba(197, 197, 197, 0.5) 118%)",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.08)",
    pointerEvents: "auto",
    width: "min-content",
});

const StyledCanvas = styled("canvas")({
    display: "block",
    borderRadius: 10,
    boxShadow: "0 4px 4px 0 rgba(0, 0, 0, 0.08), 0 8px 16px -6px rgba(23, 26, 28, 0.24)",
    backgroundColor: "white",
    cursor: "pointer",
});

const shimmerAnimation = keyframes`
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
`;

const ShimmerOverlay = styled(Box, {
    shouldForwardProp: (prop) => prop !== "visible",
})<{ visible: boolean }>(({ visible }) => ({
    position: "absolute",
    inset: 0,
    borderRadius: 10,
    overflow: "hidden",
    background: "linear-gradient(135deg, #a0a0a0, #d0d0d0)",
    opacity: visible ? 1 : 0,
    transition: "opacity 300ms ease-out",
    pointerEvents: "none",
    "&::after": {
        content: '""',
        position: "absolute",
        inset: 0,
        background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
        animation: visible ? `${shimmerAnimation} 1.5s infinite` : "none",
    },
}));

const ErrorOverlay = styled(Box, {
    shouldForwardProp: (prop) => prop !== "visible",
})<{ visible: boolean }>(({ visible }) => ({
    position: "absolute",
    inset: 0,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "white",
    opacity: visible ? 1 : 0,
    transition: "opacity 300ms ease-out",
    pointerEvents: "none",
}));

const CANVAS_WIDTH = 92;
const CANVAS_HEIGHT = 92;
// Longest-side cap for the mini map's decoded bitmap. Unlike the web mini map
// (which fits the whole drawing), the mobile mini map shows a camera-centered
// crop, so it needs more resolution than a fit-to-view thumbnail. The canvas
// can display at most CANVAS_WIDTH × dpr ÷ MOBILE_MINIMAP_VISIBLE_FRACTION
// source pixels of the longer side (92 × 4 ÷ 0.3 ≈ 1227 at dpr 4). 1536 covers
// that with margin and matches the planned `medium_preview` (RFD00134) so the
// same image can also serve as the large map's opening frame. The full decode
// happens only in the large map, and only while it's open. Retune if the
// visible fraction changes.
const MINI_MAP_MAX_IMAGE_SIZE = 1536;

// Fraction of the position indicator kept visible when it is pinned past the
// drawing edge. The indicator spans ±POSITION_INDICATOR_HALF_SIZE around its
// center, so the visible extent at a canvas edge is inset + half-size:
// 2/3 of the 24px arrow (16px) visible -> a 4px inset.
const PINNED_INDICATOR_VISIBLE_FRACTION = 2 / 3;
const INDICATOR_PIN_INSET = (2 * PINNED_INDICATOR_VISIBLE_FRACTION - 1) * POSITION_INDICATOR_HALF_SIZE;

function MiniMap({ onClick, enabled }: { onClick?: () => void; enabled: boolean }) {
    const { t } = useTranslation();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const currentDrawing = useAppSelector(selectCurrentDrawing);
    const levels = useAppSelector(selectMiniMapLevels);
    const drawings = useAppSelector(selectMiniMapDrawings);
    const placements = useAppSelector(selectDrawingPlacements);
    const tilingEnabled = useAppSelector(selectMinimapTilingEnabled);
    const isMobileDesign = useAppSelector(selectIsProcoreMobileDesign);
    const procoreContext = useAppSelector(selectProcoreContext);
    const sceneId = useAppSelector(selectSceneId);
    const navigationPin = useAppSelector(selectNavigationPin);
    const dispatch = useAppDispatch();
    // While the large map is open it fully covers the mini map, so the mini map
    // doesn't need a decoded bitmap. Release it (pass null) so only the large
    // map's full-resolution decode is resident — never both at once. It
    // re-decodes (cheaply) when the large map closes.
    const isLargeMapOpen = useAppSelector(selectIsLargeMapMobileOpen);
    const img = useDrawingImage(isLargeMapOpen ? null : currentDrawing, MINI_MAP_MAX_IMAGE_SIZE);
    const {
        state: { view, internalEventEmitter },
    } = useExplorerGlobals();

    const [drawingPosition, setDrawingPosition] = useState<DrawingSpacePosition>({
        position: vec2.fromValues(0.5, 0.5),
        rotation: 0,
    });

    const drawMap = useCallback(
        (
            ctx: CanvasRenderingContext2D,
            img: ImageBitmap,
            natural: { width: number; height: number },
            drawingPosition: DrawingSpacePosition,
        ) => {
            // A detached (closed) ImageBitmap reports width 0; drawing it throws
            // "image source is detached". Guard against a stale bitmap captured
            // before `useDrawingImage` released it.
            if (img.width === 0) {
                return;
            }
            ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

            // Base the on-screen scale on the drawing's *natural* size, not the
            // (possibly downscaled) decoded bitmap, so the zoom is identical
            // regardless of the decode resolution — we just sample from a smaller
            // bitmap. `drawImage` stretches it back up to `scaledWidth/Height`.
            // Scale is a visible fraction of the sheet (not a raw pixel factor) so
            // high-res drawings are not tighter than low-res ones (BM-12546).
            const scale = mobileMiniMapImageScale(natural.width, natural.height, CANVAS_WIDTH, CANVAS_HEIGHT);
            const scaledWidth = natural.width * scale;
            const scaledHeight = natural.height * scale;

            const { offsetX, offsetY, indicatorX, indicatorY } = clampMiniMapFollowLayout({
                scaledWidth,
                scaledHeight,
                canvasWidth: CANVAS_WIDTH,
                canvasHeight: CANVAS_HEIGHT,
                posXPercent: drawingPosition.position[0],
                posYPercent: drawingPosition.position[1],
                // Keep 2/3 of an edge-pinned indicator visible instead of half.
                indicatorInset: INDICATOR_PIN_INSET,
            });

            ctx.drawImage(img, offsetX, offsetY, scaledWidth, scaledHeight);

            drawPositionIndicator(ctx, indicatorX, indicatorY, drawingPosition.rotation);
        },
        [],
    );

    useEffect(() => {
        if (!view || !internalEventEmitter || levels.length === 0) {
            return;
        }

        const handleCameraUpdated = (camera: RenderStateCamera) => {
            const { drawingId: targetDrawingId, releasePin } = resolveSheetForCamera({
                camera,
                levels,
                placements,
                currentDrawingId: currentDrawing?.id,
                navigationPin,
            });

            if (releasePin) {
                dispatch(clearNavigationPin());
            }

            if (targetDrawingId && targetDrawingId !== currentDrawing?.id) {
                dispatch(setCurrentDrawing(targetDrawingId));

                // Fetch the drawing data for the new drawing if we don't have it already.
                // Skip the download while the mini map is turned off; it will be
                // downloaded once it becomes visible (see useMiniMapData).
                const targetDrawing = drawings[targetDrawingId];
                if (enabled && targetDrawing) {
                    const { status } = targetDrawing.drawingData.previewAsyncState;
                    if (status === AsyncStatus.Initial) {
                        downloadDrawingData({
                            dispatch,
                            drawingId: targetDrawing.id,
                            url: targetDrawing.drawingData.url,
                            telemetrySource: "miniMap.miniMapMobile.fetchDrawingData",
                            tiling: buildTilingRequest(
                                tilingEnabled,
                                procoreContext,
                                sceneId,
                                targetDrawing.drawingData,
                                isMobileDesign,
                            ),
                        });
                    }
                }
            }

            if (currentDrawing?.modelAB && currentDrawing?.drawingAB) {
                const result = modelToDrawingSpace(currentDrawing.modelAB, currentDrawing.drawingAB, camera);
                setDrawingPosition(result);
            }
        };

        handleCameraUpdated(view.renderState.camera);

        const unsubscribe = internalEventEmitter.on("cameraUpdated", handleCameraUpdated);

        return () => {
            unsubscribe();
        };
    }, [
        view,
        internalEventEmitter,
        levels,
        placements,
        navigationPin,
        currentDrawing,
        dispatch,
        drawings,
        enabled,
        tilingEnabled,
        isMobileDesign,
        procoreContext,
        sceneId,
    ]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }

        const ctx = canvas.getContext("2d");
        if (!ctx) {
            return;
        }

        const dpr = window.devicePixelRatio || 1;

        canvas.width = CANVAS_WIDTH * dpr;
        canvas.height = CANVAS_HEIGHT * dpr;
        canvas.style.width = `${CANVAS_WIDTH}px`;
        canvas.style.height = `${CANVAS_HEIGHT}px`;
        ctx.scale(dpr, dpr);

        if (!currentDrawing) {
            drawBackground(ctx, CANVAS_WIDTH, CANVAS_HEIGHT);
            return;
        }

        // The mini map only consumes the preview, so its render is driven by the
        // preview state, not the (large-map-only) tiles state.
        const previewState = currentDrawing.drawingData.previewAsyncState;
        switch (previewState.status) {
            case AsyncStatus.Initial:
            case AsyncStatus.Loading:
                drawBackground(ctx, CANVAS_WIDTH, CANVAS_HEIGHT);
                break;
            case AsyncStatus.Success:
                if (img) {
                    drawMap(
                        ctx,
                        img,
                        { width: previewState.data.width, height: previewState.data.height },
                        drawingPosition,
                    );
                } else {
                    // Preview downloaded but not decoded yet; show placeholder
                    // until `useDrawingImage` resolves the bitmap.
                    drawBackground(ctx, CANVAS_WIDTH, CANVAS_HEIGHT);
                }
                break;
            case AsyncStatus.Error:
            default:
                drawBackground(ctx, CANVAS_WIDTH, CANVAS_HEIGHT);
        }
    }, [currentDrawing, drawingPosition, drawMap, img]);

    const status = currentDrawing?.drawingData.previewAsyncState.status;
    const isLoading = !currentDrawing || status === AsyncStatus.Initial || status === AsyncStatus.Loading;
    const isError = status === AsyncStatus.Error;
    const disabled = status !== AsyncStatus.Success;

    return (
        <Box sx={{ position: "relative", width: CANVAS_WIDTH, height: CANVAS_HEIGHT }}>
            <StyledCanvas ref={canvasRef} onClick={disabled ? undefined : onClick} />
            <ShimmerOverlay visible={isLoading} role="status" aria-live="polite" aria-label={t("loadingDrawing")} />
            <ErrorOverlay visible={isError} role="alert" aria-live="polite" aria-label={t("loadingDrawingError")}>
                <ErrorIcon size="lg" />
            </ErrorOverlay>
        </Box>
    );
}

function drawBackground(ctx: CanvasRenderingContext2D, width: number, height: number) {
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, "#a0a0a0");
    gradient.addColorStop(1, "#d0d0d0");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
}

export function MiniMapMobile() {
    const resetView = useResetView();
    const dispatch = useAppDispatch();
    const currentDrawing = useAppSelector(selectCurrentDrawing);
    const isLargeMapOpen = useAppSelector(selectIsLargeMapMobileOpen);
    const isMiniMapVisible = useAppSelector(selectIsMiniMapVisible);
    const [isCompactMode, setIsCompactMode] = useState(false);
    const isOnline = useAppSelector(selectIsOnline);
    const disabled = !isOnline;

    useMiniMapData(isMiniMapVisible);

    useEffect(() => {
        const checkHeight = () => {
            setIsCompactMode(window.innerHeight < MINIMAP_LAYOUT.MINIMAP_MIN_HEIGHT);
        };

        checkHeight();
        window.addEventListener("resize", checkHeight);
        return () => window.removeEventListener("resize", checkHeight);
    }, []);

    const onHomeClick = async () => {
        await resetView();
    };

    const handleMiniMapClick = () => {
        dispatch(setLargeMapMobileOpen(true));
    };

    return (
        <>
            <MiniMapContainer id={MINIMAP_MOBILE_ELEMENT_ID}>
                <Box sx={{ display: isMiniMapVisible ? "contents" : "none" }}>
                    {isCompactMode
                        ? currentDrawing && (
                              <Button
                                  variant="secondary"
                                  icon={<Blueprint />}
                                  aria-label="Open 2D mini map"
                                  onClick={handleMiniMapClick}
                                  style={{ backgroundColor: "white" }}
                                  disabled={disabled}
                              />
                          )
                        : currentDrawing && <MiniMap onClick={handleMiniMapClick} enabled={isMiniMapVisible} />}
                </Box>

                <Button
                    variant="secondary"
                    icon={<Home />}
                    aria-label="Home"
                    onClick={onHomeClick}
                    style={{ backgroundColor: "white" }}
                />
            </MiniMapContainer>

            <LargeMapMobile open={isLargeMapOpen} />
        </>
    );
}
